import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toastService } from '../../../../services/toast';
import { coursesApi } from '../../../../services/api/courses.api';
import BasicInfo from './components/BasicInfo';
import CourseOptions from './components/CourseOptions';
import Scheduling from './components/Scheduling';
import Instructors from './components/Instructors';
import StepperHeader from './components/StepperHeader';
import { BASE_URL } from '../../../../config/axios';

// تحويل مسار الصورة إلى مسار كامل
const getImageUrl = (path) => {
  if (!path) return null;
  
  // إذا كان المسار يبدأ بـ http أو https، نستخدمه كما هو
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // إذا كان المسار يبدأ بـ /storage، نضيف عنوان الباك إند
  if (path.startsWith('/storage')) {
    return `${BASE_URL}${path}`;
  }
  
  // في الحالات الأخرى، نفترض أنه مسار نسبي ونضيف عنوان الباك إند
  return `${BASE_URL}/storage/${path}`;
};

const EditCourse = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [instructorsList, setInstructorsList] = useState([]);
  const [courseData, setCourseData] = useState({
    // Basic Info
    name: '',
    description: '',
    whatYouWillLearn: '',
    coverImage: null,
    coverImagePreview: null,
    planImage: null,
    planImagePreview: null,
    
    // Course Options
    courseType: 'free',
    price: '0',
    duration: '',
    category: '',
    status: 'draft',
    askInstructor: 'enabled',
    sections: [], // أقسام الدورة

    // Scheduling
    startDate: '',
    endDate: '',
    schedule: [],
    
    // Instructors
    instructors: []
  });



  // جلب التصنيفات والمدربين عند تحميل الصفحة
  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesResponse = await coursesApi.getCategories();
        // تم جلب التصنيفات بنجاح
        setCategories(categoriesResponse);
      } catch (error) {
        // معالجة خطأ جلب التصنيفات
        toastService.error('حدث خطأ أثناء جلب التصنيفات');
      }
    };
    
    fetchData();
  }, []);

  // جلب بيانات الدورة عند تحميل الصفحة
  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        setLoading(true);
        const response = await coursesApi.getById(id);
        
        // تحويل البيانات من الباك إند إلى الشكل المطلوب في الفرونت إند
        // التحقق من وجود البيانات في مختلف الهياكل المحتملة للاستجابة
        const courseDetails = response?.data?.data || response?.data || {};
        
        // بيانات الدورة من API
        
        // التحقق من قيمة السعر والتحويل إلى رقم
        const priceValue = parseFloat(courseDetails.price || 0);
        
        // تحديد نوع الدورة بناءً على البيانات من الباك إند
        // نستخدم course_type إذا كان موجوداً، وإلا نحدده بناءً على السعر
        let courseType = 'free';
        
        // إذا كان نوع الدورة محدد صراحةً في البيانات
        if (courseDetails.course_type) {
          courseType = courseDetails.course_type;
        } 
        // وإلا، نحدده بناءً على السعر
        else if (priceValue > 0) {
          courseType = 'paid';
        }
        
        // تحديد نوع الدورة وقيمة السعر
        
        setCourseData({
          // Basic Info
          id: courseDetails.id || id,
          name: courseDetails.name || '',
          description: courseDetails.description || '',
          whatYouWillLearn: courseDetails.whatYouWillLearn || courseDetails.what_you_will_learn || '',
          coverImage: null,
          coverImagePreview: courseDetails.cover_image ? getImageUrl(courseDetails.cover_image) : null,
          planImage: null,
          planImagePreview: courseDetails.plan_image ? getImageUrl(courseDetails.plan_image) : null,
          
          // Course Options
          courseType: courseType,
          price: courseType === 'paid' ? (priceValue > 0 ? priceValue.toString() : '1') : '0',
          duration: courseDetails.duration?.toString() || '',
          category: courseDetails.category_id?.toString() || '',
          status: courseDetails.status || 'draft',
          askInstructor: courseDetails.ask_instructor || 'enabled',
          sections: courseDetails.sections || [],
          
          // Discount Data
          hasDiscount: courseDetails.discounts && courseDetails.discounts.length > 0 && courseDetails.discounts[0].active,
          discountType: courseDetails.discounts && courseDetails.discounts.length > 0 ? courseDetails.discounts[0].discount_type : 'percentage',
          discountValue: courseDetails.discounts && courseDetails.discounts.length > 0 ? courseDetails.discounts[0].discount_value : '',
          discountStart: courseDetails.discounts && courseDetails.discounts.length > 0 ? courseDetails.discounts[0].start_date : '',
          discountEnd: courseDetails.discounts && courseDetails.discounts.length > 0 ? courseDetails.discounts[0].end_date : '',
          discount_start_date: courseDetails.discounts && courseDetails.discounts.length > 0 ? courseDetails.discounts[0].start_date : '',
          discount_end_date: courseDetails.discounts && courseDetails.discounts.length > 0 ? courseDetails.discounts[0].end_date : '',

          // Scheduling
          startDate: courseDetails.start_date || '',
          endDate: courseDetails.end_date || '',
          schedule: [],
          isComingSoon: courseDetails.is_coming_soon === 1 || courseDetails.is_coming_soon === true,
          publishDate: courseDetails.start_date || '', // استخدام start_date كتاريخ نشر افتراضي
          isPublished: courseDetails.status === 'published',
          
          // Instructors
          instructors: Array.isArray(courseDetails.instructors) ? courseDetails.instructors : []
        });
      } catch (error) {
        // معالجة خطأ جلب بيانات الدورة
        toastService.error('حدث خطأ أثناء جلب بيانات الدورة');
        navigate('/admin/dashboard/courses');
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [id, navigate]);

  const updateData = (newData) => {
    setCourseData(prev => ({
      ...prev,
      ...newData
    }));
  };

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSaveDraft = async () => {
    // التحقق من الحقول الإجبارية حسب المرحلة الحالية
    let requiredFields = [];
    let missingFields = [];
    
    // الحقول الإجبارية للمرحلة الأولى
    if (currentStep >= 1) {
      requiredFields = ['name'];
      if (!courseData.name) missingFields.push('اسم الدورة');
    }
    
    // الحقول الإجبارية للمرحلة الثانية
    if (currentStep >= 2) {
      requiredFields = [...requiredFields, 'courseType', 'category', 'duration'];
      if (!courseData.courseType) missingFields.push('نوع الدورة (مجاني/مدفوع)');
      if (!courseData.category) missingFields.push('تصنيف الدورة');
      if (!courseData.duration) missingFields.push('مدة الدورة');
    }
    
    // إذا كانت هناك حقول مفقودة، نعرض رسالة خطأ
    if (missingFields.length > 0) {
      toastService.error(`يجب إكمال الحقول التالية لحفظ المسودة: ${missingFields.join('، ')}`);
      return;
    }
    
    try {
      // حفظ كمسودة
      const updatedData = {
        ...courseData,
        status: 'draft'
      };
      
      await coursesApi.update(id, updatedData);
      
      toastService.success('تم حفظ التعديلات كمسودة بنجاح');
      navigate('/admin/dashboard/courses');
    } catch (error) {
      // معالجة خطأ حفظ المسودة
      
      // عرض رسائل خطأ محددة بناءً على استجابة الخادم
      if (error.response && error.response.data && error.response.data.errors) {
        const errors = error.response.data.errors;
        let errorMessage = 'الحقول المطلوبة لإكمال هذه المرحلة: ';
        
        if (currentStep === 1) {
          errorMessage += 'اسم الدورة';
        } else if (currentStep === 2) {
          errorMessage += 'اسم الدورة، نوع الدورة، التصنيف، المدة';
        } else if (currentStep === 3) {
          errorMessage += 'اسم الدورة، نوع الدورة، التصنيف، المدة، تاريخ البداية والنهاية';
        } else if (currentStep === 4) {
          errorMessage += 'اسم الدورة، نوع الدورة، التصنيف، المدة، تاريخ البداية والنهاية، مدرب واحد على الأقل';
        }
        
        toastService.error(errorMessage);
      } else {
        toastService.error('حدث خطأ أثناء حفظ المسودة. تأكد من إكمال الحقول المطلوبة.');
      }
    }
  };

  const handlePublish = async () => {
    try {
      // نشر الدورة
      const updatedData = {
        ...courseData,
        status: 'published'
      };
      
      await coursesApi.update(id, updatedData);
      
      toastService.success('تم نشر الدورة المعدلة بنجاح');
      navigate('/admin/dashboard/courses');
    } catch (error) {
      // معالجة خطأ نشر الدورة
      toastService.error('حدث خطأ أثناء نشر الدورة');
    }
  };

  const handleStepClick = (stepId) => {
    // لا يمكن التنقل إلى خطوة أبعد من الخطوة التالية
    if (stepId > currentStep + 1) return;
    
    // يمكن التنقل إلى أي خطوة سابقة أو الخطوة التالية
    setCurrentStep(stepId);
  };

  const handleDataUpdate = (step, newData) => {
    setCourseData(prevData => ({
      ...prevData,
      ...newData
    }));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <BasicInfo data={courseData} updateData={updateData} />;
      case 2:
        return <CourseOptions 
          data={courseData} 
          categories={categories}
          updateData={(newData) => handleDataUpdate('options', newData)} 
        />;
      case 3:
        return <Scheduling 
          data={courseData} 
          updateData={(newData) => handleDataUpdate('schedule', newData)} 
        />;
      case 4:
        return <Instructors 
          data={courseData.instructors || []} 
          updateData={(newInstructors) => handleDataUpdate('instructors', { instructors: newInstructors })} 
        />;
      default:
        return null;
    }
  };

  return (
      <div className="p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 text-center py-4">تعديل الدورة</h1>
          </div>
          
          {loading ? (
            <div className="flex flex-col items-center justify-center p-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-300">جاري تحميل بيانات الدورة...</p>
            </div>
          ) : (
            <>
              <div className={`pb-2 pt-8 px-6 ${currentStep === 1 ? '' : 'animate-fadeIn'}`}> 
                <StepperHeader currentStep={currentStep} onStepClick={handleStepClick} />
              </div>
              <div className="flex-grow pt-0 px-6 pb-6 min-h-[400px]">
                {renderStep()}
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 p-6">
                <div className="relative flex justify-center items-center">
                  <div className="flex gap-4">
                    {currentStep > 1 && (
                      <button
                        type="button"
                        onClick={handlePrev}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <span>السابق</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                    {currentStep < 4 && (
                      <button
                        type="button"
                        onClick={handleNext}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                      >
                        <span>التالي</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="absolute left-6 flex gap-4">
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h1a2 2 0 012 2v7a2 2 0 01-2 2H7a2 2 0 01-2-2V8a2 2 0 012-2h1v5.586l-1.293-1.293z" />
                      </svg>
                      <span>حفظ كمسودة</span>
                    </button>
                    <button
                      type="button"
                      onClick={handlePublish}
                      className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-green-600 dark:bg-green-500 rounded-md hover:bg-green-700 dark:hover:bg-green-600 active:bg-green-800 dark:active:bg-green-700 shadow-sm hover:shadow transition-all duration-200"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>نشر</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
  );
};

export default EditCourse;
